// Constants for the Crude Tender Opening module.
//
// Tender Opening is the business stage AFTER Procurement: a Procurement record
// that is Submitted advances into this stage (Procurement's Submit stamps
// mainStatus = 'Tender Opening'). Only records whose main status is
// 'Tender Opening' appear in this list.
//
// Phase 1 = list only. Everything runs on dummy data until the backend contract
// arrives; the values below map 1:1 to backend enums once it does.

// Fixed procurement type — carried over from Planning/Procurement, shown read-only.
export const PROCUREMENT_TYPE = 'Hydrocarbon Crude & Condensate';

// Procurement method (mekanisme pengadaan) — inherited from Procurement.
export const PROCUREMENT_METHODS = [
  'Pelelangan Khusus',
  'Pemilihan Langsung',
  'Tunjuk Langsung',
];

// Main Status — every record in this list sits in the Tender Opening stage.
export const MAIN_STATUS = 'Tender Opening';

// Contract types (carried over from Planning, shown read-only in Detail).
export const CONTRACT_TYPES = ['Term', 'Spot'];

// Default placeholder for values the counterparty/DMUT is asked to fill in
// (HPS alpha fields + the four freight fields) — shown until a real value lands.
export const REQUESTED = 'Requested';

// Tender Opening Status — the sub-status within the Tender Opening stage.
// Derived from the five checklist steps (see deriveToStatus), except Recall
// which is a manual/BE-driven terminal state seeded directly.
//   Open           -> no checklist step completed (default on new data)
//   In Progress    -> at least one, but not all, steps completed
//   Done           -> all five steps completed
//   Recall         -> pulled back after Done (terminal, read-only)
//   On Soft Recall -> DMUT invitation pulled back (Soft Recall on the DMUT tab):
//                     the DMUT checklist is un-checked, clause becomes editable.
//   On Hard Recall -> the whole request is pulled back (Hard Recall on the
//                     Panitia tab): all checklist steps are un-checked and the
//                     Planning/Procurement/Tender Opening stages all reset.
export const TO_STATUS = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
  RECALL: 'Recall',
  SOFT_RECALL: 'On Soft Recall',
  HARD_RECALL: 'On Hard Recall',
};

// Badge colours for the Tender Opening Status column.
export const TO_STATUS_BADGE = {
  [TO_STATUS.OPEN]: 'secondary',
  [TO_STATUS.IN_PROGRESS]: 'warning',
  [TO_STATUS.DONE]: 'success',
  [TO_STATUS.RECALL]: 'danger',
  [TO_STATUS.SOFT_RECALL]: 'danger',
  [TO_STATUS.HARD_RECALL]: 'danger',
};

// The five completion checklist columns (green when completed, grey by default).
// Order matches the list layout in the requirement.
export const CHECK_COLUMNS = [
  { key: 'requestHps', label: 'Request HPS' },
  { key: 'inviteDmutDocument', label: 'Invite DMUT Document' },
  { key: 'invitePanitiaTender', label: 'Invite Panitia Tender' },
  { key: 'reqFreightAvailability', label: 'Req Freight Availability' },
];

// Derive the Tender Opening Status from the checklist booleans.
// Recall is never derived here — a record already in Recall keeps it.
export const deriveToStatus = (checks = {}) => {
  const values = CHECK_COLUMNS.map(c => Boolean(checks[c.key]));
  const done = values.filter(Boolean).length;
  if (done === 0) return TO_STATUS.OPEN;
  if (done === values.length) return TO_STATUS.DONE;
  return TO_STATUS.IN_PROGRESS;
};

// Action rule (updated rule-tender-opening.md + 5-value PhaseStatus contract):
//   Edit        -> Open | In Progress | On Soft Recall
//   View Detail -> Done | On Hard Recall
// The plain `RECALL` key is kept above (with its badge) for back-compat but is no
// longer a contract value, so it drives neither predicate.
export const isEditable = status =>
  status === TO_STATUS.OPEN ||
  status === TO_STATUS.IN_PROGRESS ||
  status === TO_STATUS.SOFT_RECALL;

export const isViewable = status =>
  status === TO_STATUS.DONE || status === TO_STATUS.HARD_RECALL;

// Detail-page editability per rule §Detail.1:
//   Open           -> every Send tab editable (Requirement/Supporting excluded,
//                      handled separately below)
//   In Progress    -> only the tabs whose checklist step is still grey (not
//                      Completed) are editable; green ones become view-only
//                   Done / On Hard Recall -> everything view-only
//   On Soft Recall -> the four Send tabs stay view-only (contract endpoint 5
//                      422s until Resubmit Clause runs); only the Requirement
//                      tab's Clause Detail section is editable (see
//                      isClauseEditable) to support the Resubmit flow.
// `checkKey` is the CHECK_COLUMNS key for that Send tab (null for tabs with no
// checklist step, e.g. Requirement/Supporting Document).
export const isTabEditable = (mode, toStatus, checkKey, checks = {}) => {
  if (mode !== 'edit') return false;
  if (toStatus === TO_STATUS.DONE || toStatus === TO_STATUS.HARD_RECALL) {
    return false;
  }
  if (toStatus === TO_STATUS.SOFT_RECALL) return false;
  if (!checkKey) return false;
  if (toStatus === TO_STATUS.OPEN) return true;
  if (toStatus === TO_STATUS.IN_PROGRESS) return !checks[checkKey];
  return false;
};

// Requirement tab > Clause Detail section is the one exception carved out of
// isTabEditable above — editable ONLY during On Soft Recall (Q2), to support
// the Resubmit Clause flow (contract endpoint 10).
export const isClauseEditable = (mode, toStatus) =>
  mode === 'edit' && toStatus === TO_STATUS.SOFT_RECALL;
